import { setupOrder, type BookingFor, type SetupStepKey } from "@teamna/shared";
import type { LabelConcept } from "@teamna/web-client/hooks/use-labels.ts";

/**
 * The setup steps as the web office names them.
 *
 * The order and the "done" rule come from the API (`GET /business/businesses/:id/setup`); the
 * words come from here, because the API's titles use the neutral nouns and every string a person
 * reads has to use the business's own labels. The mobile spec section 5.2 lists the eight steps of
 * a family business. Where clients book for themselves (feature O1) there are seven: no placement,
 * and the balances step asks for the sessions left and the day each package ends. Each function
 * takes `bookingFor` (from `useShape()`).
 */

export type SetupSlug =
  "business" | "team" | "import" | "sessions" | "packages" | "invite" | "placement" | "balances";

export const SETUP_SLUGS: Record<SetupStepKey, SetupSlug> = {
  business: "business",
  team: "team",
  members_imported: "import",
  sessions: "sessions",
  packages: "packages",
  invite_sent: "invite",
  placement: "placement",
  balances: "balances",
};

/** The steps of a business of this shape, in order. The same list the API answers with. */
export function setupSteps(bookingFor: BookingFor): SetupStepKey[] {
  return setupOrder(bookingFor);
}

export function setupPath(key: SetupStepKey): string {
  return `/owner/setup/${SETUP_SLUGS[key]}`;
}

/** The number of a step, 1 first. 0 for a step this shape does not have (placement for self). */
export function stepNumber(key: SetupStepKey, bookingFor: BookingFor): number {
  return setupSteps(bookingFor).indexOf(key) + 1;
}

type Label = (concept: LabelConcept, opts?: { plural?: boolean; capital?: boolean }) => string;

/** Title and one-line description of a step, in the business's words. */
export function stepCopy(
  key: SetupStepKey,
  label: Label,
  bookingFor: BookingFor,
): { title: string; description: string } {
  const members = label("member", { plural: true });
  const participants = label("participant", { plural: true });
  const groups = label("group", { plural: true });
  switch (key) {
    case "business":
      return {
        title: "Your business",
        description: "The name, the sport, the venue and the days you are closed.",
      };
    case "team":
      return {
        title: "Team and roles",
        description: `The people who run the business with you, and the ${label("staff", { plural: true })} who run the sessions.`,
      };
    case "members_imported":
      return {
        title: `Import ${members}`,
        description: `Your ${label("member")} list, from a WhatsApp group, pasted numbers or a CSV file. Nobody is contacted.`,
      };
    case "sessions":
      return {
        title: `${label("group", { plural: true, capital: true })} and sessions`,
        description: `Your ${groups} and the weekly schedule. Your sessions come from it.`,
      };
    case "packages":
      return {
        title: "Packages and rules",
        description: `What ${members} buy, and your cancellation rule.`,
      };
    case "invite_sent":
      return {
        title: `Invite ${members}`,
        description: "One message with your join code. The join tracking shows who is in.",
      };
    case "placement":
      return {
        title: `Put ${participants} in ${groups}`,
        description: `Every ${label("participant")} in a ${label("group")}, or marked "not sure yet".`,
      };
    case "balances":
      if (bookingFor === "self") {
        return {
          title: "Sessions left and end date",
          description: `The sessions left on the package of each ${label("member")}, and the day the package ends.`,
        };
      }
      return {
        title: "Sessions left this month",
        description: `The starting number for each ${label("participant")} who joins in the middle of the month.`,
      };
  }
}

/** Why a step is not open yet, in the business's words. */
export function gateCopy(key: SetupStepKey, label: Label, bookingFor: BookingFor): string {
  switch (key) {
    case "invite_sent":
      return `Set up your sessions and packages first, so ${label("member", { plural: true })} see a real schedule and real prices when they join.`;
    case "placement":
      return `No ${label("participant", { plural: true })} yet. This step opens when a ${label("member")} adds one.`;
    case "balances":
      return bookingFor === "self"
        ? `Set up your packages first, so the sessions left of each ${label("member")} belong to a package.`
        : "Set up your packages first, so each starting number belongs to a package.";
    default:
      return "This step is not open yet.";
  }
}
